/**
 * ClientScopeGate — short-circuits client-scoped routes when the actor
 * cannot see the targeted `:clientId`. Per
 * `docs/frontend/035-frontend-app-shell-plan.md §3.2 / §5.2`:
 *
 *   - `system_admin === true` → render the "no tenant context" state. The
 *     underlying read endpoints would 404 anyway (Task 024 / 025 / etc.
 *     pattern); this short-circuits the round-trip.
 *   - leadership (owner / marketing_manager) → admit org-wide.
 *   - otherwise → admit only when the URL's `:clientId` is present in
 *     `me.client_assignments`.
 *
 * The gate is UX only. RLS remains the load-bearing security check.
 */

import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useMeContext } from '../../context/MeContextProvider';
import { canSeeClient } from '../../permissions';
import { NotFoundState, PermissionDeniedState } from '../states';

interface ClientScopeGateProps {
  readonly children: ReactNode;
}

export function ClientScopeGate({ children }: ClientScopeGateProps) {
  const { me } = useMeContext();
  const { clientId } = useParams();

  if (!me) return null;

  if (!clientId) {
    return (
      <NotFoundState
        title="معرّف العميل غير صالح"
        description="رابط هذه الصفحة لا يحتوي على معرّف عميل."
      />
    );
  }

  if (me.system_admin) {
    return (
      <PermissionDeniedState
        title="لا يوجد سياق عميل لمستخدم النظام"
        description="حسابات System admin لا تملك صلاحية فتح workspace العملاء. الميزة مخصّصة للمستخدمين التابعين لمؤسسة فعّالة."
      />
    );
  }

  if (!canSeeClient(me, clientId)) {
    return (
      <PermissionDeniedState
        title="لا تملك صلاحية فتح هذا العميل"
        description="هذا العميل غير معيّن لحسابك. تواصل مع Owner الوكالة لطلب التعيين."
      />
    );
  }

  return <>{children}</>;
}
